/** @module Start screen panels: open reviews, duplicates, top movers and the latest cards. */
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ArrowDownRight, ArrowUpRight, ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { api, type components, problemFrom } from "@/api/client";
import { CardFace } from "@/components/card-face";
import { ScanStatus } from "@/components/ui/controls";
import { isFullArt, printedAs, useCard } from "@/lib/cards";
import { money } from "@/lib/format";

type Mover = components["schemas"]["MoverOut"];
type Window = "7d" | "30d";

async function fetchParked(): Promise<number> {
  const { data, response } = await api.GET("/api/v1/review-queue", {
    params: { query: { status: "no_match", limit: 200 } },
  });
  if (!data) throw problemFrom(response);
  return data.items.length;
}

/** Open cases, split into "mehrdeutig" and "ohne Treffer", as one link into the queue. */
export function ReviewPanel({ open, compact = false }: { open: number; compact?: boolean }) {
  const { t } = useTranslation();
  const parked = useQuery({
    queryKey: ["dashboard", "parked"],
    queryFn: fetchParked,
    enabled: open > 0,
  });
  const none = parked.data ?? 0;
  const ambiguous = Math.max(0, open - none);

  if (compact) {
    return (
      <Link to="/review" className="panel panel-pad row-between text-(--text) no-underline">
        <span className="row gap-3">
          <ScanStatus state={open > 0 ? "review" : "auto"} />
          <span className="flex flex-col">
            <b>{t("dashboard.openReviews")}</b>
            <span className="t-sm t-muted">
              {open > 0
                ? t("dashboard.reviewSplit", { ambiguous, none })
                : t("dashboard.reviewNone")}
            </span>
          </span>
        </span>
        <span className="row">
          {open > 0 ? <span className="badge badge-count">{open}</span> : null}
          <ChevronRight className="size-5 text-(--text-muted)" />
        </span>
      </Link>
    );
  }
  return (
    <Link to="/review" className="panel panel-lg flex flex-col gap-3 text-(--text) no-underline">
      <div className="row-between">
        <span className="t-label">{t("dashboard.openReviews")}</span>
        {open > 0 ? <span className="badge badge-count">{open}</span> : null}
      </div>
      {open > 0 ? (
        <>
          <div className="row">
            <ScanStatus state="review" />
            <span className="t-sm">{t("dashboard.ambiguous", { count: ambiguous })}</span>
          </div>
          <div className="row">
            <ScanStatus state="none" />
            <span className="t-sm">{t("dashboard.parked", { count: none })}</span>
          </div>
          <span className="btn btn-secondary btn-sm self-start">
            {t("dashboard.reviewNow")}
            <ChevronRight />
          </span>
        </>
      ) : (
        <div className="row">
          <ScanStatus state="auto" />
          <span className="t-sm">{t("dashboard.reviewNone")}</span>
        </div>
      )}
    </Link>
  );
}

export function DuplicatesPanel() {
  const { t } = useTranslation();
  const duplicates = useQuery({
    queryKey: ["dashboard", "duplicates"],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/dashboard/duplicates", {
        params: { query: { limit: 6 } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
  });
  const rows = duplicates.data ?? [];
  if (rows.length === 0) return null;
  return (
    <div className="panel panel-lg flex flex-col gap-[10px]">
      <span className="t-label">{t("dashboard.duplicates")}</span>
      {rows.map((row) => (
        <DuplicateRow
          key={`${row.print_id}-${row.variant_id}`}
          printId={row.print_id}
          name={row.name}
          quantity={row.quantity}
        />
      ))}
    </div>
  );
}

function DuplicateRow({
  printId,
  name,
  quantity,
}: {
  printId: string;
  name: string | null;
  quantity: number;
}) {
  const card = useCard(printId);
  return (
    <Link
      to="/cards/$printId"
      params={{ printId }}
      className="row-between t-sm text-(--text) no-underline"
    >
      <span className="truncate">
        {name ?? printId} · <span className="num">{printedAs(card.data, "")}</span>
      </span>
      <span className="badge badge-qty">{quantity}×</span>
    </Link>
  );
}

async function fetchMovers(window: Window) {
  const { data, response } = await api.GET("/api/v1/dashboard/movers", {
    params: { query: { window, limit: 5 } },
  });
  if (!data) throw problemFrom(response);
  return data;
}

/** Winners and losers in one list, the biggest swing first. */
export function strongest(movers: { gainers: Mover[]; losers: Mover[] }, limit = 6): Mover[] {
  return [...movers.gainers, ...movers.losers]
    .sort((a, b) => Math.abs(b.delta_percent) - Math.abs(a.delta_percent))
    .slice(0, limit);
}

function Change({ mover }: { mover: Mover }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const up = mover.delta_percent >= 0;
  return (
    <span className={`badge ${up ? "badge-gain" : "badge-loss"}`}>
      {up ? <ArrowUpRight /> : <ArrowDownRight />}
      {up ? "+" : "−"}
      {Math.abs(mover.delta_percent).toLocaleString(locale, { maximumFractionDigits: 1 })} %
    </span>
  );
}

function MoverRow({ mover }: { mover: Mover }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const card = useCard(mover.print_id);
  return (
    <tr>
      <td>
        <Link
          to="/cards/$printId"
          params={{ printId: mover.print_id }}
          className="row gap-[10px] text-(--text) no-underline"
        >
          <span className="thumb">
            <CardFace printId={mover.print_id} name={mover.name ?? undefined} />
          </span>
          <span className="truncate font-semibold">{mover.name ?? mover.print_id}</span>
        </Link>
      </td>
      <td className="num">{printedAs(card.data, "")}</td>
      <td className="td-num">{money(mover.value, locale) ?? "–"}</td>
      <td className="td-num">
        <Change mover={mover} />
      </td>
    </tr>
  );
}

/** The phone version of a row: name and set on the left, value and change on the right. */
function MoverItem({ mover }: { mover: Mover }) {
  const { i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? "de";
  const card = useCard(mover.print_id);
  return (
    <Link
      to="/cards/$printId"
      params={{ printId: mover.print_id }}
      className="flex items-center gap-[10px] border-b border-(--border) py-2 text-(--text) no-underline"
    >
      <span className="thumb">
        <CardFace printId={mover.print_id} name={mover.name ?? undefined} />
      </span>
      <span className="flex min-w-0 grow flex-col">
        <b className="t-sm truncate">{mover.name ?? mover.print_id}</b>
        <span className="t-xs t-muted num">{printedAs(card.data, "")}</span>
      </span>
      <span className="flex flex-col items-end">
        <span className="num t-sm">{money(mover.value, locale) ?? "–"}</span>
        <Change mover={mover} />
      </span>
    </Link>
  );
}

export function MoversPanel() {
  const { t } = useTranslation();
  const [window, setWindow] = useState<Window>("7d");
  const movers = useQuery({
    queryKey: ["dashboard", "movers", window],
    queryFn: () => fetchMovers(window),
  });
  const rows = movers.data ? strongest(movers.data) : [];
  const empty = (
    <p className="t-sm t-muted py-3">
      {movers.isPending ? t("common.loading") : t("dashboard.moversEmpty")}
    </p>
  );

  return (
    <>
      <section className="flex flex-col gap-1 md:hidden">
        <h2 className="t-h3">
          {t("dashboard.topMoversShort", { range: t("dashboard.rangeShort.7d") })}
        </h2>
        {rows.length === 0
          ? empty
          : rows.map((mover) => (
              <MoverItem key={`${mover.print_id}-${mover.variant_id}`} mover={mover} />
            ))}
      </section>
      <section className="panel hidden flex-col overflow-hidden md:flex">
        <div className="row-between px-4 pt-[14px]">
          <h2 className="t-h3">{t("dashboard.topMovers")}</h2>
          <div className="tabs border-0" role="tablist" aria-label={t("dashboard.topMovers")}>
            {(["7d", "30d"] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="tab"
                aria-selected={window === option}
                className="tab"
                onClick={() => {
                  setWindow(option);
                }}
              >
                {t(`dashboard.range.${option}`)}
              </button>
            ))}
          </div>
        </div>
        {rows.length === 0 ? (
          <div className="px-4">{empty}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>{t("collection.card")}</th>
                <th>{t("collection.set")}</th>
                <th className="td-num">{t("collection.value")}</th>
                <th className="td-num">
                  {t("dashboard.delta", { range: t(`dashboard.rangeShort.${window}`) })}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((mover) => (
                <MoverRow key={`${mover.print_id}-${mover.variant_id}`} mover={mover} />
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

export function RecentPanel({ strip = false }: { strip?: boolean }) {
  const { t } = useTranslation();
  const recent = useQuery({
    queryKey: ["dashboard", "recent", strip ? 8 : 4],
    queryFn: async () => {
      const { data, response } = await api.GET("/api/v1/dashboard/recent", {
        params: { query: { limit: strip ? 8 : 4 } },
      });
      if (!data) throw problemFrom(response);
      return data;
    },
  });
  const items = recent.data ?? [];
  const cards = items.map((item) => (
    <Link
      key={item.item_id}
      to="/cards/$printId"
      params={{ printId: item.print_id }}
      className={`card-shape block ${strip ? "w-[74px] shrink-0" : ""} ${isFullArt(item.rarity_class) ? "foil" : ""}`}
      aria-label={item.name ?? item.print_id}
    >
      <CardFace printId={item.print_id} name={item.name ?? undefined} />
    </Link>
  ));
  return (
    <div className={strip ? "flex flex-col gap-[6px]" : "panel panel-lg flex flex-col gap-3"}>
      <div className="row-between">
        <h2 className="t-h3">{t("dashboard.recent")}</h2>
        <Link to="/collection" className="t-sm">
          {t("dashboard.all")}
        </Link>
      </div>
      {items.length === 0 ? (
        <p className="t-sm t-muted">
          {recent.isPending ? t("common.loading") : t("dashboard.empty")}
        </p>
      ) : strip ? (
        <div className="flex gap-[10px] overflow-x-auto pb-1">{cards}</div>
      ) : (
        <div className="grid grid-cols-4 gap-3">{cards}</div>
      )}
    </div>
  );
}
